import {
    ArrowRight,
    ChevronDown,
    KeyRound,
    MousePointerClick,
    ShieldCheck,
    UserRound,
    Wallet,
    Zap,
} from 'lucide-react';
import React from 'react';
import { Link } from 'react-router';

import { NETWORK_IDS, useTokenPairs } from '@/features/tokenPairs';
import { terminalHref } from '@/lib/landingHost';

const FAQS = [
    {
        q: 'What am I trading?',
        a: 'Perpetual futures on Hyperliquid — crypto, plus stock and commodity markets. You pick a direction and a size; the backend executes the order for you, no order book to drive, no fields to fill.',
    },
    {
        q: 'Do I need a wallet to try it?',
        a: 'No. Demo mode gives you $100 of practice money against live market prices, straight from the same price feed the real mode uses. Trading for real needs an account and a funded Snappy Wallet.',
    },
    {
        q: 'How do I sign in?',
        a: 'With Google, Discord, X, or Telegram, or with your own wallet. Once you are in you can add a passkey, and after that it is Face ID or a fingerprint instead of anything to remember.',
    },
    {
        q: 'How do deposits work?',
        a: 'You fund your Snappy Wallet with USDC once, bridged from Cronos (either USDC) or sent straight across from HyperEVM. After that every trade is executed gaslessly by the backend, no wallet popups, no gas per trade.',
    },
    {
        q: 'Can I withdraw at any time?',
        a: 'Yes, your balance is yours to take out whenever you want, back to your own wallet on Cronos or HyperEVM. Two things to know before you confirm: withdrawals have a small minimum, and a flat network fee comes off the amount. Both are shown on the withdraw screen, along with what you will actually receive.',
    },
    {
        q: 'How much leverage can I use?',
        a: 'Up to 40x on the biggest markets, but the cap is set per market and plenty of them are far lower. Whichever market you are looking at, its cap sits in the chart header, next to the price.',
    },
    {
        q: 'Can I set a take profit and a stop loss?',
        a: 'Yes, before you open and after. Pick the levels on the ticket, then change or remove them while the position is live. The triggers run server-side either way, so they fire whether or not the tab is open.',
    },
    {
        q: 'Where do I see my liquidation price?',
        a: 'Before you open, the order overview estimates one for each direction so you can see both prices before committing. Once the position is live, the chart and the positions table show Hyperliquid’s own figure, which sits a little closer than the estimate because it accounts for maintenance margin.',
    },
    {
        q: 'What fees do I pay?',
        a: 'Two, and both are charged on your position size rather than on your margin: SnappyTrade takes 0.08%, and Hyperliquid takes its own taker fee. Each one applies when you open and again when you close. The PnL shown on an open position is the exchange’s figure before the closing fee, so what you actually realise is lower. And because the fee is charged on position size, it costs a bigger share of your margin the more leverage you use.',
    },
    {
        q: 'Where does the name come from?',
        a: 'A snail that fills orders in milliseconds. The contrast is the point: everything about SnappyTrade is relaxed except execution.',
    },
];

const STEPS = [
    {
        icon: <UserRound size={18} />,
        title: 'Sign in',
        body: 'Google, Discord, X, Telegram, or your own wallet. Add a passkey afterwards and it is Face ID from then on.',
    },
    {
        icon: <Wallet size={18} />,
        title: 'Deposit once',
        body: 'Fund your Snappy Wallet with USDC, bridged from Cronos or sent direct from HyperEVM. The last popup you’ll see.',
    },
    {
        icon: <MousePointerClick size={18} />,
        title: 'Pick a side',
        body: 'Long or Short, one click. Size, leverage, and both liquidation prices are visible before you commit.',
    },
    {
        icon: <Zap size={18} />,
        title: 'Exit anytime',
        body: 'Close with one click, or the C key. Take-profit and stop-loss run server-side, and stay editable while the position is live.',
    },
];

/**
 * Shown while the live market list is in flight, and as the standing answer if
 * it never arrives. Majors only, so a stale build still reads as true.
 */
const FALLBACK_MARKETS = [
    'BTC',
    'ETH',
    'SOL',
    'XRP',
    'DOGE',
    'AVAX',
    'LINK',
    'ARB',
    'OP',
    'LTC',
    'ADA',
    'NEAR',
];

/** Enough to fill the marquee twice over without listing the whole venue. */
const MARQUEE_MARKET_COUNT = 16;

// Short git commit hash baked in at build time (vite.config.ts `define`);
// empty under vitest, where the global isn't defined.
const APP_VERSION =
    typeof __APP_COMMIT_HASH__ === 'string' ? __APP_COMMIT_HASH__ : '';

/**
 * Display ticker for a market. HIP-3 builder markets are namespaced by their
 * dex ("xyz:TSLA"); the prefix is plumbing, the ticker is the product.
 */
function tickerOf(coin: string): string {
    const separator = coin.indexOf(':');
    return separator >= 0 ? coin.slice(separator + 1) : coin;
}

/**
 * The real market list, so the marquee cannot drift from the venue the way a
 * hardcoded one does. Public read, same query the terminal already warms.
 */
function useMarketTickers(): string[] {
    const { data } = useTokenPairs(NETWORK_IDS.HYPER);
    const live = data?.markets.map((market) => tickerOf(market.coin)) ?? [];
    return live.length > 0
        ? live.slice(0, MARQUEE_MARKET_COUNT)
        : FALLBACK_MARKETS;
}

const KEYS = [
    { keys: ['L', '↑'], label: 'Long' },
    { keys: ['S', '↓'], label: 'Short' },
    { keys: ['C'], label: 'Close' },
    { keys: ['←', '→'], label: 'Size' },
    { keys: ['⌘K'], label: 'Switch market' },
];

/**
 * Marketing landing page, served at /landing. The terminal is the default
 * page at /; demo mode works there with no wallet, so every CTA links
 * straight into the product.
 */
const Landing: React.FC = () => {
    const markets = useMarketTickers();

    return (
        <div className="min-h-screen overflow-x-clip bg-background text-foreground">
            {/* Nav */}
            <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-md">
                <div className="mx-auto flex h-14 max-w-6xl items-center gap-2.5 px-5">
                    <img
                        src="/snappy-logo.png"
                        alt=""
                        aria-hidden="true"
                        className="h-7 w-auto"
                    />
                    <span className="text-base font-semibold tracking-tight">
                        Snappy
                        <span className="text-muted-foreground">Trade</span>
                    </span>
                    <nav className="ml-auto hidden items-center gap-6 text-sm text-muted-foreground sm:flex">
                        <a
                            href="#platform"
                            className="transition-colors hover:text-foreground"
                        >
                            Platform
                        </a>
                        <a
                            href="#how-it-works"
                            className="transition-colors hover:text-foreground"
                        >
                            How it works
                        </a>
                        <a
                            href="#security"
                            className="transition-colors hover:text-foreground"
                        >
                            Security
                        </a>
                        <a
                            href="#faq"
                            className="transition-colors hover:text-foreground"
                        >
                            FAQ
                        </a>
                    </nav>
                    <a
                        href={terminalHref()}
                        className="ml-4 rounded-lg bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground transition-transform hover:bg-primary/90 active:scale-[0.97] sm:ml-8"
                    >
                        Launch app
                    </a>
                </div>
            </header>

            {/* Hero */}
            <section className="relative">
                {/* Lime beam behind the product shot */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 top-24 h-[560px] bg-[radial-gradient(640px_320px_at_50%_45%,rgba(200,242,36,0.16),transparent_70%)]"
                />

                <div className="relative mx-auto max-w-6xl px-5 pt-16 text-center sm:pt-24">
                    <p className="inline-flex items-center gap-2 rounded-full border border-border px-3.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        <span className="size-1.5 rounded-full bg-primary" />
                        Demo open to everyone, no wallet needed
                    </p>
                    <h1 className="mx-auto mt-6 max-w-[14ch] text-balance text-5xl font-bold leading-[1.02] tracking-tight sm:text-7xl">
                        Slow name.{' '}
                        <span className="text-primary">Fast fills.</span>
                    </h1>
                    <p className="mx-auto mt-5 max-w-[46ch] text-base text-muted-foreground">
                        Long or short any market in one click. No wallet popups,
                        no gas per trade, no order forms with nine fields.
                        Deposit once, trade at the speed you think.
                    </p>
                    <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                        <a
                            href={terminalHref()}
                            className="flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:bg-primary/90 active:scale-[0.97]"
                        >
                            Try it, no wallet needed
                            <ArrowRight size={15} />
                        </a>
                        <a
                            href="#how-it-works"
                            className="rounded-lg border border-border px-6 py-3 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
                        >
                            See how it works
                        </a>
                    </div>

                    {/* Product shot */}
                    <div className="relative mx-auto mt-14 max-w-5xl">
                        <img
                            src="/landing/terminal-desktop.png"
                            alt="The SnappyTrade terminal: market list, live chart, and a one-click trade ticket"
                            className="w-full rounded-xl border border-border shadow-[0_32px_96px_rgba(0,0,0,0.6)] ring-1 ring-primary/10"
                        />
                        {/* Fade into the page */}
                        <div
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-background to-transparent"
                        />
                    </div>
                </div>
            </section>

            {/* Lime mega-card */}
            <section id="platform" className="scroll-mt-14">
                <div className="mx-auto max-w-6xl px-4 py-16 sm:px-5">
                    <div className="overflow-hidden rounded-[2rem] bg-[linear-gradient(180deg,#DCFB5F_0%,#C8F224_55%,#B4DF0C_100%)] text-[#181B02]">
                        <div className="px-6 pt-14 text-center sm:px-10">
                            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#181B02]/70">
                                The platform
                            </p>
                            <h2 className="mx-auto mt-3 max-w-[18ch] text-balance text-3xl font-bold tracking-tight sm:text-5xl">
                                More speed. Fewer clicks.
                            </h2>
                            <p className="mx-auto mt-3 max-w-[46ch] text-sm font-medium text-[#181B02]/70">
                                One screen, one decision. The order overview
                                answers everything before you commit, then Long
                                or Short is a single click, on desktop or in
                                your thumb's reach on mobile.
                            </p>
                            <p className="mx-auto mt-4 font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-[#181B02]/55">
                                Perps on Hyperliquid · up to 40x · live trend or
                                candles
                            </p>
                        </div>

                        <div className="mt-10 flex items-end justify-center gap-5 px-6 sm:px-12">
                            <img
                                src="/landing/terminal-desktop.png"
                                alt="Desktop terminal"
                                className="hidden w-2/3 rounded-t-xl border border-[#181B02]/25 shadow-[0_24px_64px_rgba(24,27,2,0.35)] sm:block"
                            />
                            <img
                                src="/landing/terminal-mobile.png"
                                alt="The SnappyTrade terminal on mobile, with thumb-reach Long and Short buttons"
                                className="w-3/5 max-w-[240px] rounded-t-2xl border border-[#181B02]/25 shadow-[0_24px_64px_rgba(24,27,2,0.35)] sm:w-1/5 sm:min-w-[190px]"
                            />
                        </div>

                        {/* Markets marquee */}
                        <div className="border-t border-[#181B02]/15 py-4">
                            <div className="overflow-hidden">
                                <div className="flex w-max gap-10 pl-10 font-mono text-sm font-semibold text-[#181B02]/75 motion-safe:animate-[landing-marquee_36s_linear_infinite] hover:[animation-play-state:paused] focus-within:[animation-play-state:paused]">
                                    {[...markets, ...markets].map(
                                        (market, i) => (
                                            <span
                                                key={`${market}-${i}`}
                                                className="flex items-center gap-10"
                                            >
                                                {market}-USD
                                                <span className="text-[#181B02]/25">
                                                    ·
                                                </span>
                                            </span>
                                        ),
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Feature rows */}
            <section className="mx-auto max-w-6xl px-5 py-8">
                <div className="grid items-center gap-8 md:grid-cols-2 md:gap-14">
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                            Execution
                        </p>
                        <h2 className="mt-2 max-w-[18ch] text-balance text-2xl font-semibold tracking-tight sm:text-3xl">
                            One click. The overview is the confirmation.
                        </h2>
                        <p className="mt-3 max-w-[44ch] text-sm leading-relaxed text-muted-foreground">
                            Order size, estimated entry, and the liquidation
                            price for both directions are on screen before you
                            commit. There's no confirm modal to click through,
                            by the time you press Long, you already know
                            everything a modal would ask.
                        </p>
                    </div>
                    {/* Ticket fragment */}
                    <div className="rounded-2xl border border-border bg-card p-5">
                        <div className="flex flex-col gap-1.5 rounded-lg border border-border/60 bg-secondary/30 p-3 text-[12px]">
                            {[
                                ['Notional', '$2,000.00'],
                                ['Est. entry price', '64,729.4'],
                                ['Liq. price (long)', '63,111.1'],
                            ].map(([label, value]) => (
                                <div
                                    key={label}
                                    className="flex justify-between"
                                >
                                    <span className="text-muted-foreground">
                                        {label}
                                    </span>
                                    <span className="font-mono tabular-nums text-foreground/90">
                                        {value}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-2.5">
                            <div className="flex flex-col items-center rounded-lg bg-primary py-3 font-bold text-primary-foreground">
                                <span className="text-sm">Long</span>
                                <span className="font-mono text-[9px] font-medium opacity-80">
                                    L or ↑
                                </span>
                            </div>
                            <div className="flex flex-col items-center rounded-lg border border-destructive/45 py-3 font-bold text-destructive">
                                <span className="text-sm">Short</span>
                                <span className="font-mono text-[9px] font-medium">
                                    S or ↓
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-16 grid items-center gap-8 md:grid-cols-2 md:gap-14">
                    {/* Keyboard fragment */}
                    <div className="order-last rounded-2xl border border-border bg-card p-6 md:order-first">
                        <div className="grid grid-cols-2 gap-4">
                            {KEYS.map(({ keys, label }) => (
                                <div
                                    key={label}
                                    className="flex items-center gap-3"
                                >
                                    <span className="flex gap-1.5">
                                        {keys.map((key) => (
                                            <kbd
                                                key={key}
                                                className="flex h-9 min-w-9 items-center justify-center rounded-md border border-border bg-secondary px-2 font-mono text-sm text-foreground shadow-[0_2px_0_var(--border)]"
                                            >
                                                {key}
                                            </kbd>
                                        ))}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                        {label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                            Keyboard first
                        </p>
                        <h2 className="mt-2 max-w-[18ch] text-balance text-2xl font-semibold tracking-tight sm:text-3xl">
                            Trade from the home row.
                        </h2>
                        <p className="mt-3 max-w-[44ch] text-sm leading-relaxed text-muted-foreground">
                            Every action has a key, and the buttons teach them
                            to you. Open with L or S, close with C, resize with
                            the arrow keys, switch markets with ⌘K, your hands
                            never leave the keyboard, and the mouse becomes
                            optional.
                        </p>
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section
                id="how-it-works"
                className="mx-auto max-w-6xl scroll-mt-14 px-5 py-20"
            >
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    How it works
                </p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight">
                    Four steps, then it's muscle memory
                </h2>
                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {STEPS.map((step, i) => (
                        <div
                            key={step.title}
                            className="rounded-xl border border-border bg-card p-5"
                        >
                            <div className="flex items-center gap-2.5">
                                <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                    {step.icon}
                                </span>
                                <span className="font-mono text-[11px] text-muted-foreground">
                                    0{i + 1}
                                </span>
                            </div>
                            <h3 className="mt-3 text-sm font-semibold">
                                {step.title}
                            </h3>
                            <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                                {step.body}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Security */}
            <section
                id="security"
                className="scroll-mt-14 border-t border-border"
            >
                <div className="mx-auto grid max-w-6xl gap-10 px-5 py-20 md:grid-cols-2">
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                            Security
                        </p>
                        <h2 className="mt-2 text-2xl font-semibold tracking-tight">
                            What we can do, and what we can't
                        </h2>
                        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                            Trading runs through a Snappy Wallet you fund from
                            your own wallet. The backend can execute trades with
                            that balance (that's the whole trick behind
                            one-click execution) and nothing else. Withdrawals
                            go back to your wallet, and your balance stays
                            withdrawable at all times.
                        </p>
                    </div>
                    <div className="flex flex-col gap-3">
                        {[
                            [
                                <KeyRound size={15} key="k" />,
                                'Your keys stay yours',
                                'The app never asks for seed phrases. On-chain signatures happen only in your own wallet, and only to move funds in or out.',
                            ],
                            [
                                <ShieldCheck size={15} key="s" />,
                                'Scoped execution',
                                'The Snappy Wallet can open and close positions with its balance, it cannot touch your main wallet.',
                            ],
                            [
                                <Wallet size={15} key="w" />,
                                'Withdraw anytime',
                                'Send funds back to your own wallet, on Cronos or HyperEVM, whenever you want.',
                            ],
                        ].map(([icon, title, body]) => (
                            <div
                                key={title as string}
                                className="flex gap-3 rounded-xl border border-border bg-card p-4"
                            >
                                <span className="mt-0.5 shrink-0 text-primary">
                                    {icon}
                                </span>
                                <div>
                                    <p className="text-sm font-semibold">
                                        {title}
                                    </p>
                                    <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">
                                        {body}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="scroll-mt-14 border-t border-border">
                <div className="mx-auto max-w-3xl px-5 py-20">
                    <p className="text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        FAQ
                    </p>
                    <h2 className="mt-2 text-center text-2xl font-semibold tracking-tight">
                        The real questions
                    </h2>
                    <div className="mt-8 flex flex-col gap-2">
                        {FAQS.map((faq) => (
                            <details
                                key={faq.q}
                                className="group rounded-xl border border-border bg-card px-5 py-4"
                            >
                                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold marker:content-none">
                                    {faq.q}
                                    <ChevronDown
                                        size={16}
                                        className="shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
                                    />
                                </summary>
                                <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
                                    {faq.a}
                                </p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="border-t border-border">
                <div className="mx-auto max-w-6xl px-5 py-20">
                    <div className="relative overflow-hidden rounded-[2rem] border border-border bg-black px-6 py-16 text-center">
                        <img
                            src="/snappy-logo.png"
                            alt=""
                            aria-hidden="true"
                            className="pointer-events-none absolute -right-8 top-1/2 hidden w-72 -translate-y-1/2 opacity-30 md:block"
                        />
                        <h2 className="mx-auto max-w-[20ch] text-balance text-3xl font-bold tracking-tight sm:text-4xl">
                            Trade in seconds. Deposit in one.
                        </h2>
                        <p className="mx-auto mt-3 max-w-[42ch] text-sm text-muted-foreground">
                            Start in demo mode with live prices and $100 of
                            practice money. No wallet, no sign-up, nothing to
                            lose but the excuse.
                        </p>
                        <a
                            href={terminalHref()}
                            className="mt-7 inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:bg-primary/90 active:scale-[0.97]"
                        >
                            Launch SnappyTrade
                            <ArrowRight size={15} />
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-border">
                <div className="mx-auto flex max-w-6xl flex-col items-center gap-3 px-5 py-8 text-xs text-muted-foreground sm:flex-row">
                    <div className="flex items-center gap-2">
                        <img
                            src="/snappy-logo.png"
                            alt=""
                            aria-hidden="true"
                            className="h-4 w-auto"
                        />
                        <span>SnappyTrade</span>
                    </div>
                    <span className="sm:ml-auto">
                        Leveraged trading involves substantial risk of loss.
                    </span>
                    <nav className="flex items-center gap-4">
                        <Link
                            to="/terms"
                            className="transition-colors hover:text-foreground"
                        >
                            Terms
                        </Link>
                        <Link
                            to="/privacy"
                            className="transition-colors hover:text-foreground"
                        >
                            Privacy
                        </Link>
                        {APP_VERSION ? (
                            <span
                                title="Build version"
                                className="font-mono text-[11px] opacity-60"
                            >
                                {APP_VERSION}
                            </span>
                        ) : null}
                    </nav>
                </div>
            </footer>
        </div>
    );
};

Landing.displayName = 'Landing';
export default Landing;
